import React, { useState } from "react";
import { toast } from "react-toastify";
import { Modal, Button } from "@/Components/Common/Tailwind";
import { QRCodeSVG } from "qrcode.react";
import { API, BACKEND_HOST } from "../../api";
import { getAccessToken } from "../../api/auth";
import {
  getPurchaseGiftBadgeLabel,
  getReservationCancelConfirmMessage,
  getReservationEffectiveCancelAmount,
} from "../../pages/UserPage/purchaseList/actionVisibility.mjs";

type PurchaseOption = {
  name: string;
  price: string | number;
  type?: string;
  state?: string;
};

type PurchaseRecordInfo = {
  id: string | number;
  name: string;
  reservationDate: string;
  reservationEndDate?: string;
  spot: string;
  assigned_seat?: {
    id: number;
    name: string;
  } | null;
  options?: PurchaseOption[];
  amount: number;
  cancelAmount: number;
  effectiveCancelAmount?: number;
  orderDate?: string | Date;
  cancelFeeGraceActive?: boolean;
  state: string;
  depositRequired?: boolean;
  precoupon_used?: boolean;
  precouponUsed?: boolean;
};

interface PurchaseRecordModalProps {
  visible: boolean;
  setVisible: (value: boolean) => void;
  info: PurchaseRecordInfo;
  setInfo: (value: Partial<PurchaseRecordInfo>) => void;
  needRefresh: () => void;
  onDateChange?: () => void;
  onGift?: () => void;
  onEditOptions?: () => void;
  isCancel?: boolean;
}

const PurchaseRecordModal = (props: PurchaseRecordModalProps) => {
  const [checkQrCode, setCheckQrCode] = useState("");
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const { info } = props;
  const totalOptionAmount = info.options
    ? info.options.reduce((sum, item) => sum + Number(item.price ?? 0), 0)
    : 0;
  const effectiveCancelAmount = getReservationEffectiveCancelAmount(info);
  const cancelConfirmMessage = getReservationCancelConfirmMessage(info);
  const isPrecouponReservation = Boolean(info.precoupon_used ?? info.precouponUsed);
  const isCancelDisabled = !isPrecouponReservation && effectiveCancelAmount === 0;
  const canUseReservationActions = info.state === "예약";
  const giftBadgeLabel = getPurchaseGiftBadgeLabel(info);

  const togModal = () => {
    props.setVisible(!props.visible);
    setCheckQrCode("");
    setShowCancelConfirm(false);
  };

  const cancelReserv = () => {
    const accessToken = getAccessToken();
    setCancelLoading(true);

    API.post(
      "/package/cancel-reservation/",
      { id: info.id },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      }
    )
      .then((response) => {
        const responseData = response.data as { error?: string };
        if (response.status === 200) {
          toast.success("취소 요청이 처리 되었습니다.");
          setShowCancelConfirm(false);
          props.setInfo({});
          props.setVisible(false);
          props.needRefresh();
        } else if (responseData?.error) {
          toast.error("취소 요청이 처리되지 않았습니다. " + responseData.error);
        } else {
          toast.error("취소 요청이 처리되지 않았습니다. " + JSON.stringify(responseData ?? response));
        }
      })
      .catch((error) => {
        toast.error("취소 요청이 처리되지 않았습니다. " + error.toString());
      })
      .finally(() => {
        setCancelLoading(false);
      });
  };

  const getQrCode = async () => {
    const accessToken = getAccessToken();
    const _res = await API.get(`${BACKEND_HOST}/package/reservation-qr/${info.id}`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    });
    return _res.data;
  };

  return (
    <React.Fragment>
      <Modal toggle={togModal} isOpen={props.visible} centered>
        <div>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            {giftBadgeLabel ? (
              <span className="inline-flex rounded-full bg-rose-100 px-3 py-1 text-xs font-semibold text-rose-600">
                {giftBadgeLabel}
              </span>
            ) : <span />}
            {!info.depositRequired && canUseReservationActions ? (
              <div className="flex justify-center">
                {checkQrCode ? (
                  <QRCodeSVG value={checkQrCode} />
                ) : (
                  <Button
                    onClick={() => {
                      getQrCode().then((obj) => {
                        setCheckQrCode(obj.qr);
                      });
                    }}
                    variant="primary"
                    style={{
                      backgroundColor: "var(--color-warning)",
                      borderColor: "var(--color-warning)",
                      color: "var(--color-text-primary)",
                    }}
                  >
                    QR 생성하기
                  </Button>
                )}
              </div>
            ) : null}
          </div>
          <ul className="list-group">
            <li className="list-group-item">
              <span className="font-semibold w-24 inline-block">예약ID :</span>
              <span>{info.id}</span>
            </li>
            <li className="list-group-item">
              <span className="font-semibold w-24 inline-block">예약상품 :</span>
              <span>{info.name}</span>
            </li>
            {info.reservationEndDate && info.reservationDate === info.reservationEndDate ?
              <li className="list-group-item">
                <span className="font-semibold w-24 inline-block">예약일 :</span>
                <span>{info.reservationDate}</span>
              </li> :
              <li className="list-group-item">
                <span className="font-semibold w-24 inline-block">예약일 :</span>
                <span>{info.reservationDate} ~ {info.reservationEndDate}</span>
              </li>}
            <li className="list-group-item">
              <span className="font-semibold w-24 inline-block">자리 :</span>
              <span>{info.spot?.includes("virtual") ? "자유석" : info.spot}</span>
            </li>
            {info.assigned_seat?.name ? (
              <li className="list-group-item">
                <span className="font-semibold w-24 inline-block">배정 자리 :</span>
                <span>{info.assigned_seat.name}</span>
              </li>
            ) : null}
            {info.options && info.options.length ? (
              <li className="list-group-item">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
                  <span className="font-semibold w-24 shrink-0">옵션상품 :</span>
                  <div className="w-full rounded-md border border-gray-200 bg-gray-50">
                    <ul className="max-h-48 overflow-y-auto divide-y divide-gray-200">
                      {info.options.map((item, idx) => (
                        <li
                          key={`${item.name}-${idx}`}
                          className="flex items-start justify-between gap-3 px-3 py-2 text-sm"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="wrap-break-word text-gray-900">{item.name}</div>
                            {item.state ? (
                              <div className="mt-1 text-xs text-gray-500">상태: {item.state}</div>
                            ) : null}
                          </div>
                          <span className="shrink-0 whitespace-nowrap text-gray-600">
                            {Number(item.price ?? 0).toLocaleString()}원
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </li>
            ) : null}
            <li className="list-group-item">
              <span className="font-semibold inline-block mr-2">
                결제금액{info.options && info.options.length ? "(옵션포함)" : ""} :
              </span>
              <span>{(info.amount + totalOptionAmount).toLocaleString()}원</span>
            </li>
            <li className="list-group-item">
              <span className="font-semibold inline-block mr-2">
                예약취소시 취소금액{info.options && info.options.length ? "(옵션포함)" : ""} :
              </span>
              <span>{effectiveCancelAmount.toLocaleString()}원</span>
            </li>
            {canUseReservationActions && info.options && info.options.length ? (
              <li className="list-group-item text-sm text-gray-600">
                예약 환불 시 연결된 미사용 옵션도 함께 환불됩니다.
              </li>
            ) : null}
            <li className="list-group-item">
              <span className="font-semibold w-24 inline-block">상태 :</span>
              <span>{info.state}</span>
            </li>
          </ul>
          {info.depositRequired && !props.isCancel && canUseReservationActions ? (
            <div className="mt-4 text-center">
              <p className="font-medium mb-2">체크인 하기전에 청소보증금을 결제해 주세요.</p>
              <p className="text-sm leading-relaxed text-gray-600">
                <i className="ri-information-line align-middle text-lg text-(--color-primary) mr-1"></i>
                <span className="font-medium text-(--color-primary)">청소보증금이란?</span>
                <br />
                청결한 시설 관리 및 비품 관리를 위해 패키지 상품 구매 시 청소보증금이 결제됩니다.
                <br />
                시설 이용 후 정리를 완료하고 체크아웃 버튼을 누르면 관리자가 정리 사항을 확인합니다.
                <br />
                <span className="text-red-500 font-medium">체크아웃 요청이 승인 되면 자동 환불됩니다.</span>
              </p>
            </div>
          ) : null}
        </div>
        {canUseReservationActions ? (
          <>
            {showCancelConfirm ? (
              <div
                className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-center text-sm font-medium leading-relaxed text-red-700"
                role="alert"
              >
                {cancelConfirmMessage.split("\n").map((line) => (
                  <div key={line}>{line}</div>
                ))}
              </div>
            ) : null}
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              {showCancelConfirm ? (
                <>
                  <Button
                    variant="secondary"
                    disabled={cancelLoading}
                    onClick={() => setShowCancelConfirm(false)}
                  >
                    돌아가기
                  </Button>
                  <Button
                    variant="danger"
                    disabled={cancelLoading}
                    onClick={cancelReserv}
                  >
                    {cancelLoading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm align-middle"
                          style={{ marginRight: "8px" }}
                          role="status"
                          aria-hidden="true"
                        />
                        처리 중...
                      </>
                    ) : "예약취소"}
                  </Button>
                </>
              ) : (
                <>
                  {props.onDateChange ? (
                    <Button variant="secondary" onClick={props.onDateChange}>
                      예약변경
                    </Button>
                  ) : null}
                  {props.onEditOptions ? (
                    <Button variant="secondary" onClick={props.onEditOptions}>
                      옵션 추가/변경
                    </Button>
                  ) : null}
                  {props.onGift ? (
                    <Button variant="secondary" onClick={props.onGift}>
                      선물하기
                    </Button>
                  ) : null}
                  <Button
                    variant="danger"
                    disabled={isCancelDisabled || cancelLoading}
                    onClick={() => setShowCancelConfirm(true)}
                  >
                    {isCancelDisabled ? "취소 불가" : "예약취소"}
                  </Button>
                </>
              )}
              {!showCancelConfirm && info.depositRequired ? (
                <Button
                  variant="primary"
                  onClick={() => {
                    window.location.href = `/add-options-order?id=${info.id}&deposit=true`;
                  }}
                >
                  청소보증금 결제
                </Button>
              ) : null}
            </div>
          </>
        ) : null}
        <div className="flex justify-center gap-2 mt-4">
          <Button
            variant="outline"
            className="bg-white"
            disabled={cancelLoading}
            onClick={() => {
              props.setInfo({});
              props.setVisible(!props.visible);
              setCheckQrCode("");
              setShowCancelConfirm(false);
            }}
          >
            닫기
          </Button>
        </div>
      </Modal>
    </React.Fragment>
  );
};
export default PurchaseRecordModal;
